import type { ComponentProps, ReactNode } from 'react';
import {
  Archive,
  CircleCheck,
  Copy,
  Ellipsis,
  Info,
  OctagonAlert,
  Plus,
  Search,
  Trash2,
  TriangleAlert,
} from 'lucide-react';

import { useBreadcrumbs } from '@/components/Breadcrumbs/useBreadcrumbs';
import {
  PhotoGallery,
  type GalleryPhoto,
} from '@/components/PhotoGallery/PhotoGallery';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ButtonGroup, ButtonGroupText } from '@/components/ui/button-group';
import { Calendar } from '@/components/ui/calendar';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
} from '@/components/ui/input-group';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import { Label } from '@/components/ui/label';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Toggle } from '@/components/ui/toggle';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Routes } from '@/utils/routes';

/**
 * The admin's living component library (webapps/admin/DESIGN.md → "The /design
 * page"). Every component in the target set is shown here from its real code,
 * in its states, with the one-line rule for when to use it. Components join
 * one at a time; a component missing here is not part of the target set yet.
 */

type BadgeVariant = NonNullable<ComponentProps<typeof Badge>['variant']>;
type ButtonVariant = NonNullable<ComponentProps<typeof Button>['variant']>;
type ButtonSize = NonNullable<ComponentProps<typeof Button>['size']>;

const BUTTON_VARIANTS: { variant: ButtonVariant; use: string }[] = [
  { variant: 'default', use: 'the one next step' },
  { variant: 'secondary', use: 'every other action' },
  { variant: 'outline', use: 'toolbars, filters' },
  { variant: 'ghost', use: 'in rows and menus' },
  { variant: 'destructive', use: 'delete, discard' },
  { variant: 'link', use: 'inline navigation' },
];

const BUTTON_SIZES: ButtonSize[] = ['xs', 'sm', 'default', 'lg'];
const ICON_BUTTON_SIZES: ButtonSize[] = [
  'icon-xs',
  'icon-sm',
  'icon',
  'icon-lg',
];

const STATUS_COLOURS = [
  { token: 'success', use: 'Live, active, done', swatch: 'tw:bg-success' },
  { token: 'warning', use: 'Needs attention', swatch: 'tw:bg-warning' },
  { token: 'info', use: 'Explanations, neutral news', swatch: 'tw:bg-info' },
  { token: 'subtle', use: 'Draft, secondary info', swatch: 'tw:bg-subtle' },
  { token: 'destructive', use: 'Errors only', swatch: 'tw:bg-destructive' },
];

const BADGES: { variant: BadgeVariant; label: string; use: string }[] = [
  { variant: 'success', label: 'Active', use: 'live / active' },
  { variant: 'subtle', label: 'Draft', use: 'draft / neutral info' },
  { variant: 'warning', label: 'Out of stock', use: 'needs attention' },
  { variant: 'info', label: 'Scheduled', use: 'informational state' },
  { variant: 'secondary', label: 'Inactive', use: 'inactive / archived' },
  { variant: 'destructive', label: 'Failed', use: 'errors only' },
];

const samplePhoto = (id: string, fill: string): GalleryPhoto => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="900"><rect width="1200" height="900" fill="${fill}"/></svg>`;
  const url = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  return { id, thumbnail_url: url, url_full_size: url };
};

const GALLERY_PHOTOS: GalleryPhoto[] = [
  samplePhoto('1', 'oklch(0.640 0.130 188.890)'),
  samplePhoto('2', 'oklch(0.595 0.130 230.318)'),
  samplePhoto('3', 'oklch(0.635 0.075 308.300)'),
];

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function DesignPage() {
  useBreadcrumbs({
    id: 'design',
    items: [{ label: 'Design', href: Routes.design() }],
  });

  return (
    <div className="tw:flex tw:flex-col tw:gap-6 tw:pt-6">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <h1 className="tw:text-2xl tw:font-bold tw:tracking-tight tw:text-foreground">
          Design
        </h1>
        <p className="tw:text-base tw:text-muted-foreground">
          The admin&rsquo;s target components, rendered from the real code.
          Rules and reasons live in <code>webapps/admin/DESIGN.md</code>.
        </p>
      </div>

      <Section
        title="Status colours"
        description="The theme's status colours, shared with the shop. Use them instead of Tailwind's stock palette (green-600, amber-600, red-50…)."
      >
        <div className="tw:grid tw:grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] tw:gap-3">
          {STATUS_COLOURS.map(({ token, use, swatch }) => (
            <div key={token} className="tw:flex tw:items-center tw:gap-3">
              <span
                className={`tw:size-8 tw:shrink-0 tw:rounded-md ${swatch}`}
              />
              <span className="tw:flex tw:flex-col">
                <code className="tw:text-sm">{token}</code>
                <span className="tw:text-sm tw:text-muted-foreground">
                  {use}
                </span>
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Button"
        description="One filled primary per page — the thing the user came to do. Everything else is secondary; destructive only for actions that delete or discard."
      >
        <div className="tw:flex tw:flex-col tw:gap-6">
          <div className="tw:flex tw:flex-wrap tw:gap-x-6 tw:gap-y-4">
            {BUTTON_VARIANTS.map(({ variant, use }) => (
              <div
                key={variant}
                className="tw:flex tw:flex-col tw:items-start tw:gap-1.5"
              >
                <Button variant={variant}>
                  {variant[0].toUpperCase() + variant.slice(1)}
                </Button>
                <span className="tw:text-xs tw:text-muted-foreground">
                  <code>{variant}</code> · {use}
                </span>
              </div>
            ))}
          </div>
          <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-x-6 tw:gap-y-4">
            {BUTTON_SIZES.map((size) => (
              <div
                key={size}
                className="tw:flex tw:flex-col tw:items-start tw:gap-1.5"
              >
                <Button size={size} variant="secondary">
                  <Plus data-icon="inline-start" />
                  New product
                </Button>
                <code className="tw:text-xs tw:text-muted-foreground">
                  {size}
                </code>
              </div>
            ))}
            {ICON_BUTTON_SIZES.map((size) => (
              <div
                key={size}
                className="tw:flex tw:flex-col tw:items-start tw:gap-1.5"
              >
                <Button size={size} variant="outline" aria-label="Add">
                  <Plus />
                </Button>
                <code className="tw:text-xs tw:text-muted-foreground">
                  {size}
                </code>
              </div>
            ))}
          </div>
          <div className="tw:flex tw:flex-wrap tw:gap-3">
            <Button disabled>Disabled</Button>
            <Button variant="secondary" disabled>
              Disabled
            </Button>
            <Button variant="outline" aria-invalid>
              Invalid
            </Button>
          </div>
        </div>
      </Section>

      <Section
        title="Input"
        description="32px, label above, helper text below (see Forms in DESIGN.md). Invalid state comes from aria-invalid, never a hand-set red border."
      >
        <div className="tw:grid tw:max-w-2xl tw:grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] tw:gap-3">
          <Input placeholder="Placeholder" />
          <Input defaultValue="Filled" />
          <Input defaultValue="Disabled" disabled />
          <Input defaultValue="Invalid" aria-invalid />
          <Input type="file" />
        </div>
      </Section>

      <Section
        title="Select"
        description="One choice from a short, fixed list. Same 32px as an input; sm (28px) only inside dense toolbars."
      >
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <Select defaultValue="active">
            <SelectTrigger className="tw:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Status</SelectLabel>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived" disabled>
                  Archived
                </SelectItem>
              </SelectGroup>
              <SelectSeparator />
              <SelectItem value="all">All statuses</SelectItem>
            </SelectContent>
          </Select>
          <Select>
            <SelectTrigger className="tw:w-44">
              <SelectValue placeholder="Placeholder" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="a">Option A</SelectItem>
            </SelectContent>
          </Select>
          <Select defaultValue="a">
            <SelectTrigger size="sm" className="tw:w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="a">Small</SelectItem>
            </SelectContent>
          </Select>
          <Select disabled>
            <SelectTrigger className="tw:w-36">
              <SelectValue placeholder="Disabled" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="a">Option A</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Section
        title="Textarea"
        description="Multi-line text. Grows with its content from a 64px minimum; same border, focus and invalid states as an input."
      >
        <div className="tw:grid tw:max-w-2xl tw:grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] tw:gap-3">
          <Textarea placeholder="Placeholder" />
          <Textarea defaultValue="Disabled" disabled />
          <Textarea defaultValue="Invalid" aria-invalid />
        </div>
      </Section>

      <Section
        title="Card"
        description="Groups one topic on a page: a title, a one-line description, then content. A ring, not a shadow — cards don't float. sm for dense side columns."
      >
        <div className="tw:grid tw:grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] tw:items-start tw:gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Default</CardTitle>
              <CardDescription>16px padding and gaps.</CardDescription>
              <CardAction>
                <Button variant="ghost" size="icon-sm" aria-label="Add">
                  <Plus />
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent>Content sits on the card colour.</CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>With footer</CardTitle>
              <CardDescription>Footer is a muted strip.</CardDescription>
            </CardHeader>
            <CardContent>Content.</CardContent>
            <CardFooter className="tw:justify-end">
              <Button size="sm">Save</Button>
            </CardFooter>
          </Card>
          <Card size="sm">
            <CardHeader>
              <CardTitle>Small</CardTitle>
              <CardDescription>12px padding and gaps.</CardDescription>
            </CardHeader>
            <CardContent>Content.</CardContent>
          </Card>
        </div>
      </Section>

      <Section
        title="Dialog"
        description="Only when the user must choose before continuing — save conflicts, confirming an irreversible action. Long content scrolls inside DialogBody; the footer stays put."
      >
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="secondary">Open dialog</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Archive product?</DialogTitle>
              <DialogDescription>
                It disappears from the shop. Orders that contain it keep it.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <Input placeholder="Reason (optional)" />
            </DialogBody>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button variant="destructive">Archive</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Section>

      <Section
        title="Dropdown menu"
        description="The … overflow menu and other action lists. Destructive actions go last, behind a separator, in the destructive variant."
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" aria-label="More actions">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="tw:w-52">
            <DropdownMenuLabel>Product</DropdownMenuLabel>
            <DropdownMenuItem>
              <Copy />
              Duplicate
              <DropdownMenuShortcut>⌘D</DropdownMenuShortcut>
            </DropdownMenuItem>
            <DropdownMenuItem disabled>
              <Archive />
              Archive
            </DropdownMenuItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Move to</DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem>Shop</DropdownMenuItem>
                <DropdownMenuItem>Events</DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem checked>
              Show in shop
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive">
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Section>

      <Section
        title="Popover"
        description="A small, non-blocking panel anchored to its trigger — quick edits and pickers. Anything that needs an answer before continuing is a dialog."
      >
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline">Open popover</Button>
          </PopoverTrigger>
          <PopoverContent align="start">
            <PopoverHeader>
              <PopoverTitle>Stock threshold</PopoverTitle>
              <PopoverDescription>
                Warn when stock drops below this.
              </PopoverDescription>
            </PopoverHeader>
            <Input type="number" defaultValue={20} />
          </PopoverContent>
        </Popover>
      </Section>

      <Section
        title="Tabs"
        description="Switches between views of the same object. default has no track behind it (an admin choice); line underlines the active tab."
      >
        <div className="tw:flex tw:flex-col tw:gap-6">
          <Tabs defaultValue="details">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="prices">Prices</TabsTrigger>
              <TabsTrigger value="history" disabled>
                History
              </TabsTrigger>
            </TabsList>
            <TabsContent value="details">
              <code>default</code> variant.
            </TabsContent>
            <TabsContent value="prices">Prices.</TabsContent>
          </Tabs>
          <Tabs defaultValue="details">
            <TabsList variant="line">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="prices">Prices</TabsTrigger>
            </TabsList>
            <TabsContent value="details">
              <code>line</code> variant.
            </TabsContent>
            <TabsContent value="prices">Prices.</TabsContent>
          </Tabs>
        </div>
      </Section>

      <Section
        title="Checkbox"
        description="On/off choices that take effect on save. Groups stack vertically, label to the right."
      >
        <div className="tw:flex tw:flex-col tw:gap-3">
          {[
            { id: 'cb-on', label: 'Checked', checked: true },
            { id: 'cb-off', label: 'Unchecked', checked: false },
            { id: 'cb-dis', label: 'Disabled', checked: true, disabled: true },
            { id: 'cb-inv', label: 'Invalid', checked: false, invalid: true },
          ].map(({ id, label, checked, disabled, invalid }) => (
            <div key={id} className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                id={id}
                defaultChecked={checked}
                disabled={disabled}
                aria-invalid={invalid}
              />
              <Label htmlFor={id}>{label}</Label>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Sheet"
        description="A side panel for a secondary task that keeps the page in view — filters, a quick look at a related object."
      >
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="outline">Open sheet</Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Filters</SheetTitle>
              <SheetDescription>Narrow down the order list.</SheetDescription>
            </SheetHeader>
            <div className="tw:px-4">
              <Input placeholder="Customer" />
            </div>
            <SheetFooter>
              <Button>Apply</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </Section>

      <Section
        title="Field"
        description="Every form control sits in a Field: label above (14px, 500), helper text below in muted 14px, the error under that. Related fields group in a FieldSet with a legend."
      >
        <FieldSet className="tw:max-w-md">
          <FieldLegend>Product</FieldLegend>
          <FieldDescription>Shown in the shop.</FieldDescription>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="field-name">Name</FieldLabel>
              <Input id="field-name" defaultValue="Bowl 1.2 l" />
              <FieldDescription>Up to 60 characters.</FieldDescription>
            </Field>
            <Field data-invalid>
              <FieldLabel htmlFor="field-sku">SKU</FieldLabel>
              <Input id="field-sku" defaultValue="BWL" aria-invalid />
              <FieldError>SKU must be at least 6 characters.</FieldError>
            </Field>
            <Field orientation="horizontal">
              <Checkbox id="field-active" defaultChecked />
              <FieldLabel htmlFor="field-active">Active</FieldLabel>
            </Field>
          </FieldGroup>
        </FieldSet>
      </Section>

      <Section
        title="Accordion"
        description="Collapses secondary detail — advanced settings, long help text. Never hides something the user must fill in."
      >
        <Accordion type="single" collapsible className="tw:max-w-md">
          <AccordionItem value="a">
            <AccordionTrigger>Advanced settings</AccordionTrigger>
            <AccordionContent>Rarely changed options.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="b">
            <AccordionTrigger>Shipping rules</AccordionTrigger>
            <AccordionContent>Per-country overrides.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </Section>

      <Section
        title="Switch"
        description="On/off settings that take effect immediately. A choice that waits for Save is a checkbox."
      >
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6">
          {[
            { id: 'sw-on', label: 'On', checked: true },
            { id: 'sw-off', label: 'Off', checked: false },
            { id: 'sw-sm', label: 'Small', checked: true, size: 'sm' as const },
            { id: 'sw-dis', label: 'Disabled', checked: true, disabled: true },
          ].map(({ id, label, checked, size, disabled }) => (
            <div key={id} className="tw:flex tw:items-center tw:gap-2">
              <Switch
                id={id}
                size={size}
                defaultChecked={checked}
                disabled={disabled}
              />
              <Label htmlFor={id}>{label}</Label>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Empty"
        description="What a list or panel shows when it has nothing yet: what would be here, and the action that fills it."
      >
        <Empty className="tw:border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Archive />
            </EmptyMedia>
            <EmptyTitle>No products yet</EmptyTitle>
            <EmptyDescription>
              Products you create appear here.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button size="sm">
              <Plus data-icon="inline-start" />
              New product
            </Button>
          </EmptyContent>
        </Empty>
      </Section>

      <Section
        title="Item"
        description="One row in a short list that isn't a table — a linked resource, a setting with its action."
      >
        <Item variant="outline" className="tw:max-w-md">
          <ItemContent>
            <ItemTitle>Packaging manual</ItemTitle>
            <ItemDescription>PDF · updated last week</ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button variant="outline" size="sm">
              Open
            </Button>
          </ItemActions>
        </Item>
      </Section>

      <Section
        title="Hover card"
        description="A preview on hover for a linked object (a customer, an order). Nothing in it may be the only way to reach information."
      >
        <HoverCard>
          <HoverCardTrigger asChild>
            <Button variant="link" className="tw:px-0">
              Order #10432
            </Button>
          </HoverCardTrigger>
          <HoverCardContent>
            <div className="tw:font-medium">Order #10432</div>
            <div className="tw:text-muted-foreground">3 items · shipped</div>
          </HoverCardContent>
        </HoverCard>
      </Section>

      <Section
        title="Calendar"
        description="Date picking, usually inside a popover next to the date field. 28px day cells."
      >
        <Calendar mode="single" className="tw:w-fit tw:rounded-lg tw:border" />
      </Section>

      <Section
        title="Alert dialog"
        description="Confirms an irreversible action. Says what happens, and the confirm button names the action — never “OK”."
      >
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive">Delete product</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogMedia>
                <Trash2 />
              </AlertDialogMedia>
              <AlertDialogTitle>Delete this product?</AlertDialogTitle>
              <AlertDialogDescription>
                It is removed from the shop and can&rsquo;t be restored.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction variant="destructive">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Section>

      <Section
        title="Toggle"
        description="A pressed/unpressed button for a view option (bold, show archived). Pressed is a muted fill."
      >
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <Toggle size="sm" aria-label="Archived" defaultPressed>
            <Archive />
            Small
          </Toggle>
          <Toggle aria-label="Archived" defaultPressed>
            <Archive />
            Pressed
          </Toggle>
          <Toggle variant="outline" aria-label="Archived">
            <Archive />
            Outline
          </Toggle>
          <Toggle size="lg" aria-label="Archived">
            <Archive />
            Large
          </Toggle>
        </div>
      </Section>

      <Section
        title="Toggle group"
        description="One choice from two to four short options that switch a view (day / week / month)."
      >
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6">
          <ToggleGroup type="single" variant="outline" defaultValue="week">
            <ToggleGroupItem value="day">Day</ToggleGroupItem>
            <ToggleGroupItem value="week">Week</ToggleGroupItem>
            <ToggleGroupItem value="month">Month</ToggleGroupItem>
          </ToggleGroup>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            defaultValue="day"
          >
            <ToggleGroupItem value="day">Day</ToggleGroupItem>
            <ToggleGroupItem value="week">Week</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </Section>

      <Section
        title="Button group"
        description="Joins buttons, or a field and its button, into one control — a split action, a search with its submit."
      >
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-6">
          <ButtonGroup>
            <Button variant="outline">Export</Button>
            <Button variant="outline" size="icon" aria-label="Export options">
              <Ellipsis />
            </Button>
          </ButtonGroup>
          <ButtonGroup>
            <ButtonGroupText>EUR</ButtonGroupText>
            <Input placeholder="Amount" className="tw:w-32" />
          </ButtonGroup>
        </div>
      </Section>

      <Section
        title="Pagination"
        description="Page links under a list that isn't a DataTable. 32px page buttons."
      >
        <Pagination className="tw:justify-start">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious href="#" />
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#">1</PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationLink href="#" isActive>
                2
              </PaginationLink>
            </PaginationItem>
            <PaginationItem>
              <PaginationEllipsis />
            </PaginationItem>
            <PaginationItem>
              <PaginationNext href="#" />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </Section>

      <Section
        title="Input group"
        description="An input with an icon, unit or inline button inside its border — search fields, amounts with a currency."
      >
        <div className="tw:grid tw:max-w-2xl tw:grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] tw:gap-3">
          <InputGroup>
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput placeholder="Search orders" />
          </InputGroup>
          <InputGroup>
            <InputGroupInput placeholder="0.00" />
            <InputGroupAddon align="inline-end">
              <InputGroupText>EUR</InputGroupText>
            </InputGroupAddon>
          </InputGroup>
          <InputGroup>
            <InputGroupInput defaultValue="VYTAL-10" />
            <InputGroupAddon align="inline-end">
              <InputGroupButton size="xs">Copy</InputGroupButton>
            </InputGroupAddon>
          </InputGroup>
          <InputGroup>
            <InputGroupInput defaultValue="Disabled" disabled />
          </InputGroup>
        </div>
      </Section>

      <Section
        title="Command"
        description="Searchable action list — the command palette (CommandDialog) and pickers built on it."
      >
        <Command className="tw:max-w-sm tw:rounded-lg tw:border">
          <CommandInput placeholder="Type a command" />
          <CommandList>
            <CommandEmpty>Nothing found.</CommandEmpty>
            <CommandGroup heading="Go to">
              <CommandItem>
                <Search />
                Orders
              </CommandItem>
              <CommandItem>
                <Archive />
                Products
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Create">
              <CommandItem>
                <Plus />
                New product
                <CommandShortcut>⌘N</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </Section>

      <Section
        title="Table"
        description="Header row on muted at 50%, foreground text at weight 500; body rows tint on hover, the header row doesn't."
      >
        <div className="tw:max-w-2xl tw:overflow-hidden tw:rounded-lg tw:border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Country</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Bowl 1250 ml</TableCell>
                <TableCell>Food</TableCell>
                <TableCell>All</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Cup 0.4 l</TableCell>
                <TableCell>Drinks</TableCell>
                <TableCell>DE, AT</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Section>

      <Section
        title="Badge"
        description="A status as text on a 10% tint of its colour. One variant per meaning — never a hand-picked colour."
      >
        <div className="tw:flex tw:flex-wrap tw:gap-x-6 tw:gap-y-4">
          {BADGES.map(({ variant, label, use }) => (
            <div
              key={variant}
              className="tw:flex tw:flex-col tw:items-start tw:gap-1.5"
            >
              <Badge variant={variant}>{label}</Badge>
              <span className="tw:text-xs tw:text-muted-foreground">
                <code>{variant}</code> · {use}
              </span>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Alert"
        description="Inline notes inside a page or card: a tint of the status colour, text in the normal colour. Never a dialog for something that needs no answer."
      >
        <div className="tw:grid tw:gap-3 tw:max-w-2xl">
          <Alert variant="info">
            <Info />
            <AlertTitle>Prices come from Stripe</AlertTitle>
            <AlertDescription>
              <code>info</code> — explains how something works.
            </AlertDescription>
          </Alert>
          <Alert variant="success">
            <CircleCheck />
            <AlertTitle>Import finished</AlertTitle>
            <AlertDescription>
              <code>success</code> — confirms a result that stays on the page.
            </AlertDescription>
          </Alert>
          <Alert variant="warning">
            <TriangleAlert />
            <AlertTitle>German text missing</AlertTitle>
            <AlertDescription>
              <code>warning</code> — something needs attention before the next
              step.
            </AlertDescription>
          </Alert>
          <Alert variant="destructive">
            <OctagonAlert />
            <AlertTitle>Couldn&rsquo;t load the orders</AlertTitle>
            <AlertDescription>
              <code>destructive</code> — an error, and what to do about it.
            </AlertDescription>
          </Alert>
          <Alert>
            <Info />
            <AlertTitle>Default</AlertTitle>
            <AlertDescription>
              <code>default</code> — neutral content on the card colour.
            </AlertDescription>
          </Alert>
        </div>
      </Section>

      <Section
        title="Photo gallery"
        description="Read-only photos attached to a record. Thumbnails open a lightbox with previous/next (arrow keys work). Hide the gallery when there are no photos."
      >
        <PhotoGallery photos={GALLERY_PHOTOS} className="tw:max-w-2xl" />
      </Section>
    </div>
  );
}
